import * as THREE from 'three/webgpu';
import {
  Fn,
  uniform,
  uv,
  positionLocal,
  vec2,
  vec3,
  vec4,
  float,
  abs,
  length,
  smoothstep,
  mix,
  sin,
  floor,
  step,
  clamp,
  dot,
  max,
  Discard,
  If,
} from 'three/tsl';
import {
  DEFAULT_VISUAL_QUALITY_PRESET,
  type VisualQualityPreset,
} from './visualConfig';
import { setEmissiveNode, hash11, hash21b } from './tslUtils';
import { FormationActivationBurst } from './FormationActivationBurst';
import { GuardianTauntAura } from './cardSignatures/GuardianTauntAura';
import { SniperReticleLaser } from './cardSignatures/SniperReticleLaser';
import { HealerBeaconRing } from './cardSignatures/HealerBeaconRing';
import { SupportBuffAura } from './cardSignatures/SupportBuffAura';
import { InterceptorDashTrail } from './cardSignatures/InterceptorDashTrail';
import { OrbitRing } from './cardSignatures/OrbitRing';
import { ChoreoBurst } from './cardSignatures/ChoreoBurst';
import { ConvertShotFlash } from './cardSignatures/ConvertShotFlash';
import { BrScatterSplash } from './cardSignatures/BrScatterSplash';
import type { LivePositionResolver } from './cardSignatures/CardSignatureEffect';
import { AlgorithmCardType } from '../../shared/types';

interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export const CARD_ACTIVATION_COLORS: readonly number[] = [
  0x4db4ff,
  0xff8a3b,
  0x9bc6ff,
  0xa78bff,
  0x6affad,
  0xffe45a,
  0xff4d4d,
  0x4dffd2,
  0xb86bff,
  0x47e0ff,
  0xff6cbb,
  0xffe066,
  0x57c1ff,
  0xa1ff96,
];

interface ExplosionEffect {
  obj: THREE.Points;
  material: THREE.PointsNodeMaterial;
  geometry: THREE.BufferGeometry;
  positions: Float32Array;
  velocities: Float32Array;
  uProgress: { value: number };
  uOpacity: { value: number };
  uColor: { value: THREE.Color };
  active: boolean;
  life: number;
  maxLife: number;
}

interface RingEffect {
  obj: THREE.Mesh;
  material: THREE.MeshBasicNodeMaterial;
  uProgress: { value: number };
  uColor?: { value: THREE.Color };
  uImpactDir?: { value: THREE.Vector3 };
  uSeed?: { value: number };
  active: boolean;
  life: number;
  maxLife: number;
  variant?: 'ally' | 'self';
}

export const HEAL_AURA_COLOR_ALLY = 0x3ba85a;
export const HEAL_AURA_COLOR_SELF = 0xffd24a;

interface BurstEffect {
  obj: THREE.Points;
  material: THREE.PointsNodeMaterial;
  geometry: THREE.BufferGeometry;
  positions: Float32Array;
  velocities: Float32Array;
  uProgress: { value: number };
  uColor: { value: THREE.Color };
  active: boolean;
  life: number;
  maxLife: number;
}

/** Pickup sparkle: vertical-bias particle burst tinted by pickup type. */
interface SparkleEffect {
  obj: THREE.Points;
  material: THREE.PointsNodeMaterial;
  geometry: THREE.BufferGeometry;
  positions: Float32Array;
  velocities: Float32Array;
  uProgress: { value: number };
  uColor: { value: THREE.Color };
  active: boolean;
  life: number;
  maxLife: number;
}

/** Hit spark: tiny, fast white→yellow particles emitted at impact point. */
interface HitSparkEffect {
  obj: THREE.Points;
  material: THREE.PointsNodeMaterial;
  geometry: THREE.BufferGeometry;
  positions: Float32Array;
  velocities: Float32Array;
  uProgress: { value: number };
  active: boolean;
  life: number;
  maxLife: number;
}

/** Pickup tint table — Drone (cyan) vs Algorithm (gold). Indexed by PickupType. */
export const PICKUP_SPARKLE_COLORS: readonly number[] = [
  0x66e0ff, // PickupType.Drone
  0xffd24a, // PickupType.Algorithm
];

/** Smoke-and-debris puff fired alongside the main explosion when a drone dies.
 * Particles are pooled like the explosion pool but use a longer lifetime,
 * stronger downward gravity, and a darker color ramp. */
interface PuffEffect {
  obj: THREE.Points;
  material: THREE.PointsNodeMaterial;
  geometry: THREE.BufferGeometry;
  positions: Float32Array;
  velocities: Float32Array;
  uProgress: { value: number };
  uOpacity: { value: number };
  active: boolean;
  life: number;
  maxLife: number;
}

/**
 * Radial dust burst spawned at a heavy-hit impact. Particles fan out along
 * the attacker→victim direction, lose altitude under gravity, and fade over
 * ~400ms. Smaller and faster than the destruction puff so several can stack
 * without obscuring gameplay.
 */
interface KnockbackDustEffect {
  obj: THREE.Points;
  material: THREE.PointsNodeMaterial;
  geometry: THREE.BufferGeometry;
  positions: Float32Array;
  velocities: Float32Array;
  uProgress: { value: number };
  active: boolean;
  life: number;
  maxLife: number;
}

/**
 * Per-hit debris burst at the impact point. ~10 particles fan outward in a
 * hemisphere oriented along attacker→victim direction. Shorter-lived
 * (~250ms) than the knockback dust so the spark "pops" rather than smolders.
 * Color uniform is orange-yellow for normal hits, white-yellow for crits;
 * the per-instance value is set inline in {@link spawnImpactDebris}.
 */
interface ImpactDebrisEffect {
  obj: THREE.Points;
  material: THREE.PointsNodeMaterial;
  geometry: THREE.BufferGeometry;
  positions: Float32Array;
  velocities: Float32Array;
  uProgress: { value: number };
  uColor: { value: THREE.Color };
  active: boolean;
  life: number;
  maxLife: number;
}

/**
 * Cyan→white shimmer wave layered on top of the existing shield-break ring.
 * Uses a thin radial band with a per-channel offset (chromatic aberration)
 * so the second pass reads as a distinct, sharper "snap" of energy
 * instead of just another concentric ring.
 */
interface ShieldShimmerEffect {
  obj: THREE.Mesh;
  material: THREE.MeshBasicNodeMaterial;
  uProgress: { value: number };
  active: boolean;
  life: number;
  maxLife: number;
}

/**
 * Directionally-biased damage flash. The ring is brightest on the front
 * hemisphere (in the impact direction) and dim on the back so the player
 * can feel which way the hit came from. Layered on top of the existing
 * symmetric {@link showDamageFlash} when an attacker position is known.
 */
interface DirectionalFlashEffect {
  obj: THREE.Mesh;
  material: THREE.MeshBasicNodeMaterial;
  uProgress: { value: number };
  uImpactDir: { value: THREE.Vector3 };
  active: boolean;
  life: number;
  maxLife: number;
}

function buildExplosionNodes(
  uProgress: ReturnType<typeof uniform>,
  uOpacity: ReturnType<typeof uniform>,
  uColor: ReturnType<typeof uniform>,
) {
  const sizeNode = Fn(() => {
    const p = float(uProgress);
    const grow = p.mul(2.2).add(1.0).mul(p.oneMinus());
    return float(1.2).mul(grow).mul(60.0);
  })();

  const colorFn = Fn(() => {
    // pointUV is invalid WGSL; 1px WebGPU points need no circle mask
    const soft = float(1.0);
    const hotWhite = vec3(1.0, 0.96, 0.82);
    const smokeGray = vec3(0.4, 0.4, 0.42);
    const fireBase = mix(hotWhite, vec3(uColor), float(uProgress));
    const highlight = float(0.35).mul(float(uProgress).oneMinus()); // d=0 (center) in 1px points
    const fireColor = fireBase.add(vec3(highlight, highlight, highlight));
    const smokeBlend = smoothstep(0.3, 0.85, float(uProgress));
    const rgb = mix(fireColor, smokeGray, smokeBlend);
    const alpha = soft.mul(float(uOpacity));
    return vec4(rgb, alpha);
  });

  const colorNode = colorFn();
  return { sizeNode, colorNode };
}

function buildHealNodes(
  uProgress: ReturnType<typeof uniform>,
  uColor: ReturnType<typeof uniform>,
) {
  const colorFn = Fn(() => {
    const u = uv();
    const uOffset = u.sub(vec2(0.5, 0.5));
    const r = length(uOffset).mul(2.0);
    const ringCenter = float(0.2).add(float(uProgress).mul(1.0));
    const ringWidth = float(0.06);
    const ring = smoothstep(float(0.04), float(0.0), abs(r.sub(ringCenter)).sub(ringWidth));
    // crosshair
    const axH = abs(u.x.sub(0.5));
    const axV = abs(u.y.sub(0.5));
    const crossH = smoothstep(float(0.005), float(0.0), axH)
      .mul(smoothstep(float(0.35), float(0.15), axV));
    const crossV = smoothstep(float(0.005), float(0.0), axV)
      .mul(smoothstep(float(0.35), float(0.15), axH));
    const crosshair = crossH.add(crossV).mul(0.6);
    const fade = float(uProgress).oneMinus();
    const alpha = ring.add(crosshair.mul(fade)).mul(fade).mul(0.85);
    If(alpha.lessThan(0.01), () => {
      Discard();
    });
    const rgb = vec3(uColor).mul(float(1.0).add(ring.mul(0.3)));
    return vec4(rgb, alpha);
  });

  const colorNode = colorFn();
  return { colorNode };
}

function buildDamageNodes(
  uProgress: ReturnType<typeof uniform>,
  uImpactDir: ReturnType<typeof uniform>,
) {
  const colorFn = Fn(() => {
    const localPos = positionLocal;
    const norm = localPos.normalize();
    const impactLen = length(vec3(uImpactDir));
    const impactPt = vec3(uImpactDir).div(impactLen.add(0.0001));
    // Fallback branch when no impact direction is provided
    //   distFromImpact = length(norm - impactPt)          if impactLen > 0.01
    //                  = length(localPos) * 1.9           otherwise
    const hasImpact = step(0.01, impactLen);
    const distA = length(norm.sub(impactPt));
    const distB = length(localPos).mul(1.9);
    const distFromImpact = mix(distB, distA, hasImpact);
    const ringCenter = float(0.1).add(float(uProgress).mul(1.6));
    const ringWidth = float(0.14);
    const ring = smoothstep(float(0.08), float(0.0), abs(distFromImpact.sub(ringCenter)).sub(ringWidth));
    const amberWhite = vec3(0.95, 0.85, 0.6);
    const hotCore = vec3(1.0, 0.95, 0.8);
    const rgb = mix(amberWhite, hotCore, ring.mul(0.55));
    const alpha = ring.mul(float(uProgress).oneMinus()).mul(0.92);
    If(alpha.lessThan(0.01), () => {
      Discard();
    });
    return vec4(rgb, alpha);
  });

  const colorNode = colorFn();
  return { colorNode };
}

/**
 * Shield-break flash: a fast-expanding white ring on a sphere, fading to a
 * cool cyan-white core. Mirrors {@link buildDamageNodes} geometry/timing but
 * swaps the amber/hot palette for a pale cyan-white that reads as "free
 * save" rather than "took damage" against the dark arena background.
 */
function buildShieldBreakNodes(uProgress: ReturnType<typeof uniform>) {
  const colorFn = Fn(() => {
    const localPos = positionLocal;
    const distFromCenter = length(localPos).mul(1.9);
    const ringCenter = float(0.1).add(float(uProgress).mul(1.6));
    const ringWidth = float(0.18);
    const ring = smoothstep(
      float(0.10),
      float(0.0),
      abs(distFromCenter.sub(ringCenter)).sub(ringWidth),
    );
    const paleCyan = vec3(0.72, 0.89, 1.0);
    const white = vec3(1.0, 1.0, 1.0);
    const rgb = mix(paleCyan, white, ring.mul(0.65));
    const alpha = ring.mul(float(uProgress).oneMinus()).mul(0.95);
    If(alpha.lessThan(0.01), () => {
      Discard();
    });
    return vec4(rgb, alpha);
  });
  const colorNode = colorFn();
  return { colorNode };
}

/** Crit ring flash: a thin golden-orange ring that expands and fades over
 *  ~220ms, drawn flat (RingGeometry rotated to face up like the heal aura)
 *  with additive blending so it pops against the arena. Uses the same
 *  uv-radial pattern as the heal nodes but a much narrower band and a
 *  hotter palette. */
function buildCritRingNodes(uProgress: ReturnType<typeof uniform>) {
  const colorFn = Fn(() => {
    const u = uv();
    const r = length(u.sub(vec2(0.5, 0.5))).mul(2.0);
    // Band is thin so the ring reads as a sharp expanding pulse, not a glow.
    const ringCenter = float(0.55).add(float(uProgress).mul(0.45));
    const ringWidth = float(0.12);
    const ring = smoothstep(float(0.10), float(0.0), abs(r.sub(ringCenter)).sub(ringWidth));
    const goldHot = vec3(1.0, 0.92, 0.55);     // bright core (hot white-gold)
    const goldDeep = vec3(1.0, 0.65, 0.18);    // outer gold-orange
    const rgb = mix(goldDeep, goldHot, ring.mul(0.85));
    const alpha = ring.mul(float(uProgress).oneMinus()).mul(0.95);
    If(alpha.lessThan(0.01), () => {
      Discard();
    });
    return vec4(rgb, alpha);
  });
  const colorNode = colorFn();
  return { colorNode };
}

function buildBurstNodes(
  uProgress: ReturnType<typeof uniform>,
  uColor: ReturnType<typeof uniform>,
) {
  const sizeNode = Fn(() => {
    const p = float(uProgress);
    const base = float(0.85).add(p.oneMinus().mul(1.4));
    return base.mul(55.0);
  })();

  const colorFn = Fn(() => {
    // pointUV is invalid WGSL; 1px WebGPU points need no circle mask
    const spark = float(1.0);
    const rgb = mix(vec3(1.0, 1.0, 1.0), vec3(uColor), 0.8);
    const alpha = spark.mul(float(uProgress).oneMinus());
    return vec4(rgb, alpha);
  });

  const colorNode = colorFn();
  return { sizeNode, colorNode };
}

/** Smoke puff: starts dark, fades to transparent gray. Particles grow slightly
 *  as they linger in air. Uses non-additive blending so the cloud reads as
 *  smoke rather than fire. */
function buildPuffNodes(
  uProgress: ReturnType<typeof uniform>,
  uOpacity: ReturnType<typeof uniform>,
) {
  const sizeNode = Fn(() => {
    // Particles grow ~30% as they age; visually conveys dispersion.
    const grow = float(1.0).add(float(uProgress).mul(0.4));
    return float(34.0).mul(grow);
  })();

  const colorFn = Fn(() => {
    const dark = vec3(0.18, 0.18, 0.20);
    const lightGray = vec3(0.45, 0.45, 0.48);
    const rgb = mix(dark, lightGray, float(uProgress));
    // Strong fade at end of life — smoke clears, doesn't linger as a hard edge.
    const fade = float(uProgress).oneMinus();
    const alpha = float(uOpacity).mul(fade).mul(0.85);
    return vec4(rgb, alpha);
  });

  const colorNode = colorFn();
  return { sizeNode, colorNode };
}

/** Pickup sparkle nodes: bright tinted particle that shrinks gently as it
 *  ages. Color is pickup-type tint mixed toward white at the start of life. */
function buildSparkleNodes(
  uProgress: ReturnType<typeof uniform>,
  uColor: ReturnType<typeof uniform>,
) {
  const sizeNode = Fn(() => {
    const p = float(uProgress);
    // Particles start large (~45px) and shrink to ~12px over their life.
    const shrink = float(1.0).sub(p.mul(0.7));
    return float(45.0).mul(shrink);
  })();

  const colorFn = Fn(() => {
    // Begin near-white-hot, fade toward the pickup tint, then fade out.
    const tint = vec3(uColor);
    const rgb = mix(vec3(1.0, 1.0, 1.0), tint, float(uProgress));
    const alpha = float(uProgress).oneMinus();
    return vec4(rgb, alpha);
  });

  const colorNode = colorFn();
  return { sizeNode, colorNode };
}

/** Hit spark nodes: small bright particle, white→yellow gradient, fast fade. */
function buildHitSparkNodes(
  uProgress: ReturnType<typeof uniform>,
) {
  const sizeNode = Fn(() => {
    const p = float(uProgress);
    // Small, fast — start ~14px and shrink quickly.
    const shrink = float(1.0).sub(p.mul(0.85));
    return float(14.0).mul(shrink);
  })();

  const colorFn = Fn(() => {
    // White at birth, yellow at death — classic muzzle/impact spark gradient.
    const white = vec3(1.0, 1.0, 1.0);
    const yellow = vec3(1.0, 0.85, 0.35);
    const rgb = mix(white, yellow, float(uProgress));
    const alpha = float(uProgress).oneMinus();
    return vec4(rgb, alpha);
  });

  const colorNode = colorFn();
  return { sizeNode, colorNode };
}

/**
 * Knockback dust nodes: small dusty-gray particle that grows slightly as it
 * ages, then fades. NormalBlending (not Additive) so the burst reads as
 * kicked-up dust rather than energy.
 */
function buildKnockbackDustNodes(
  uProgress: ReturnType<typeof uniform>,
) {
  const sizeNode = Fn(() => {
    const p = float(uProgress);
    // Dust grows ~20% as it disperses, then fades to nothing.
    const grow = float(1.0).add(p.mul(0.2));
    return float(20.0).mul(grow);
  })();

  const colorFn = Fn(() => {
    // Tan-ish dust kicked up by the impact.
    const dustEarly = vec3(0.7, 0.6, 0.45);
    const dustLate  = vec3(0.4, 0.36, 0.32);
    const rgb = mix(dustEarly, dustLate, float(uProgress));
    const alpha = float(uProgress).oneMinus().mul(0.85);
    return vec4(rgb, alpha);
  });

  const colorNode = colorFn();
  return { sizeNode, colorNode };
}

/**
 * Impact debris nodes: small sparks that start hot/large then shrink and
 * fade. Color is a uniform tint (orange-yellow normal / white-yellow crit)
 * so a single shader handles both variants without per-particle data.
 */
function buildImpactDebrisNodes(
  uProgress: ReturnType<typeof uniform>,
  uColor: ReturnType<typeof uniform>,
) {
  const sizeNode = Fn(() => {
    const p = float(uProgress);
    // Start ~16px, shrink to ~5px so the spark visibly contracts as it ages.
    const shrink = float(1.0).sub(p.mul(0.7));
    return float(16.0).mul(shrink);
  })();

  const colorFn = Fn(() => {
    // Hot core (white) at birth, fade through tint, end transparent.
    const tint = vec3(uColor);
    const rgb = mix(vec3(1.0, 1.0, 1.0), tint, float(uProgress));
    const alpha = float(uProgress).oneMinus();
    return vec4(rgb, alpha);
  });

  const colorNode = colorFn();
  return { sizeNode, colorNode };
}

/**
 * Shield-shimmer nodes: a thin radial band that expands like the cyan ring
 * but uses a faint per-channel offset to fake chromatic aberration. The R
 * and B channels sample the radius shifted by ~+/-0.005 so the band edge
 * fringes red on one side and blue on the other for a brief CRT-like beat.
 */
function buildShieldShimmerNodes(uProgress: ReturnType<typeof uniform>) {
  const colorFn = Fn(() => {
    const localPos = positionLocal;
    const r = length(localPos).mul(1.9);
    const ringCenter = float(0.05).add(float(uProgress).mul(1.5));
    const ringWidth = float(0.07);

    // Three sampled radii, offset by +/-0.005 in R/B for a chromatic edge.
    const distR = abs(r.add(0.005).sub(ringCenter)).sub(ringWidth);
    const distG = abs(r.sub(ringCenter)).sub(ringWidth);
    const distB = abs(r.sub(0.005).sub(ringCenter)).sub(ringWidth);

    const rChan = smoothstep(float(0.05), float(0.0), distR);
    const gChan = smoothstep(float(0.05), float(0.0), distG);
    const bChan = smoothstep(float(0.05), float(0.0), distB);

    // Blend the three channels into RGB; the green/cyan core dominates,
    // R fringes "warm" and B fringes "cool" at the ring edges.
    const tintR = mix(float(0.7), float(1.0), rChan);
    const tintG = mix(float(0.85), float(1.0), gChan);
    const tintB = mix(float(0.95), float(1.0), bChan);
    const rgb = vec3(tintR, tintG, tintB);

    // Average alpha so the band has an even silhouette but the colors
    // separate at the leading/trailing edge.
    const alphaCore = rChan.add(gChan).add(bChan).mul(float(1.0 / 3.0));
    const alpha = alphaCore.mul(float(uProgress).oneMinus()).mul(0.9);
    If(alpha.lessThan(0.01), () => {
      Discard();
    });
    return vec4(rgb, alpha);
  });
  const colorNode = colorFn();
  return { colorNode };
}

/**
 * Directional damage-flash nodes: a ring that expands like the standard
 * damage flash but is brighter on the side facing the impact direction
 * and dimmer on the opposite side. The brightness scalar uses a saturated
 * dot(normal, uImpactDir) so the front/back gradient is smooth.
 */
function buildDirectionalFlashNodes(
  uProgress: ReturnType<typeof uniform>,
  uImpactDir: ReturnType<typeof uniform>,
) {
  const colorFn = Fn(() => {
    const localPos = positionLocal;
    const norm = localPos.normalize();
    const r = length(localPos).mul(1.9);
    const ringCenter = float(0.1).add(float(uProgress).mul(1.6));
    const ringWidth = float(0.14);
    const ring = smoothstep(
      float(0.08),
      float(0.0),
      abs(r.sub(ringCenter)).sub(ringWidth),
    );

    // Front/back gradient: front (aligned with uImpactDir) at full brightness,
    // back at ~30%. Clamp keeps the back hemisphere visible enough to read as
    // a ring rather than a half-disc.
    const impactLen = length(vec3(uImpactDir));
    const dirNorm = vec3(uImpactDir).div(impactLen.add(0.0001));
    const facing = clamp(dot(norm, dirNorm), -1.0, 1.0);
    const hasImpact = step(0.01, impactLen);
    // map facing [-1,1] to [0.3, 1.0] when we have a direction, else 1.0.
    const biasMapped = max(facing.mul(0.5).add(0.5).mul(0.7).add(0.3), float(0.3));
    const bias = mix(float(1.0), biasMapped, hasImpact);

    const amber = vec3(0.95, 0.6, 0.25);
    const hot = vec3(1.0, 0.9, 0.55);
    const rgb = mix(amber, hot, ring.mul(0.6)).mul(bias);
    const alpha = ring.mul(float(uProgress).oneMinus()).mul(bias).mul(0.9);
    If(alpha.lessThan(0.01), () => {
      Discard();
    });
    return vec4(rgb, alpha);
  });
  const colorNode = colorFn();
  return { colorNode };
}

function buildGlitchNodes(
  uProgress: ReturnType<typeof uniform>,
  uSeed: ReturnType<typeof uniform>,
) {
  const hash1 = hash11;
  const hash2 = hash21b;

  const colorFn = Fn(() => {
    const intensity = clamp(sin(float(uProgress).mul(3.14159)).mul(1.2), 0.0, 1.0);
    const uvVar = uv().toVar();

    const blockY = floor(uvVar.y.mul(40.0));
    const blockShift = hash1(blockY.mul(13.7).add(float(uSeed))).sub(0.5).mul(0.12).mul(intensity);
    const shouldShift = step(0.7, hash1(blockY.mul(7.3).add(float(uSeed)).add(1.0)));
    uvVar.x = uvVar.x.add(blockShift.mul(shouldShift));

    const rgbSplit = float(0.015).mul(intensity);
    const rChan = smoothstep(0.5, 0.0, length(uvVar.sub(vec2(0.5, 0.5)))).mul(0.5);
    const gChan = smoothstep(
      0.5,
      0.0,
      length(uvVar.add(vec2(rgbSplit, 0.0)).sub(vec2(0.5, 0.5))),
    ).mul(0.5);
    const bChan = smoothstep(
      0.5,
      0.0,
      length(uvVar.sub(vec2(rgbSplit, 0.0)).sub(vec2(0.5, 0.5))),
    ).mul(0.5);

    const corruptX = floor(uvVar.x.mul(8.0));
    const corruptY = floor(uvVar.y.mul(6.0));
    const corruptBlock = step(0.85, hash2(vec2(corruptX, corruptY).add(float(uSeed))));
    const corruptIntensity = corruptBlock.mul(0.4).mul(intensity);

    const rOut = rChan.add(corruptIntensity.mul(0.8));
    const gOut = gChan.add(corruptIntensity.mul(0.2));
    const bOut = bChan.add(corruptIntensity.mul(0.1));
    const rgb = vec3(rOut, gOut, bOut).toVar();

    const scanline = sin(uvVar.y.mul(300.0).add(float(uSeed).mul(50.0))).mul(0.08).mul(intensity);
    rgb.addAssign(vec3(scanline, scanline, scanline));

    const alpha = intensity.mul(0.7);
    If(alpha.lessThan(0.01), () => {
      Discard();
    });
    return vec4(rgb, alpha);
  });

  const colorNode = colorFn();
  return { colorNode };
}

export class EffectsManager {
  private readonly scene: THREE.Scene;
  private readonly explosionPool: ExplosionEffect[] = [];
  private readonly healPool: RingEffect[] = [];
  private readonly damagePool: RingEffect[] = [];
  private readonly shieldPool: RingEffect[] = [];
  private readonly critPool: RingEffect[] = [];
  private critCursor = 0;
  /** DOM overlay used for the brief screen-corner flicker on crit. Lazily
   *  built on first call so unit tests that never call showCritFlash do
   *  not touch the document. */
  private critOverlay: HTMLDivElement | null = null;
  private critOverlayTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly burstPool: BurstEffect[] = [];
  private readonly glitchPool: RingEffect[] = [];
  private readonly puffPool: PuffEffect[] = [];
  private readonly puffParticleCount: number;
  private puffCursor = 0;
  private readonly sparklePool: SparkleEffect[] = [];
  private readonly sparkleParticleCount: number;
  private sparkleCursor = 0;
  private readonly hitSparkPool: HitSparkEffect[] = [];
  private readonly hitSparkParticleCount: number;
  private hitSparkCursor = 0;

  private readonly knockbackDustPool: KnockbackDustEffect[] = [];
  private readonly knockbackDustParticleCount: number;
  private knockbackDustCursor = 0;

  private readonly impactDebrisPool: ImpactDebrisEffect[] = [];
  private readonly impactDebrisParticleCount: number;
  private impactDebrisCursor = 0;
  private readonly shieldShimmerPool: ShieldShimmerEffect[] = [];
  private shieldShimmerCursor = 0;
  private readonly directionalFlashPool: DirectionalFlashEffect[] = [];
  private directionalFlashCursor = 0;

  private readonly explosionParticleCount: number;
  private explosionCursor = 0;
  private healCursor = 0;
  private damageCursor = 0;
  private shieldCursor = 0;
  private burstCursor = 0;
  private glitchCursor = 0;
  private readonly formationBurst: FormationActivationBurst;
  /** One-shot, self-disposing card-signature effects. Each implements
   * `update(ms): boolean` (returns true while alive) and `dispose()`.
   * Pumped from update(); finished entries are removed in-place. */
  private readonly cardSignatures: Array<{
    update(deltaMs: number): boolean;
    dispose(): void;
  }> = [];

  constructor(
    scene: THREE.Scene,
    qualityPreset: VisualQualityPreset = DEFAULT_VISUAL_QUALITY_PRESET,
  ) {
    this.scene = scene;
    this.explosionParticleCount = Math.max(18, Math.round(24 * qualityPreset.vfxParticleScale));
    // ~30 particles per puff per the spec; scale with quality preset.
    this.puffParticleCount = Math.max(18, Math.round(30 * qualityPreset.vfxParticleScale));

    this.createExplosionPool(Math.round(12 * qualityPreset.vfxParticleScale));
    this.createHealPool(Math.round(8 * qualityPreset.vfxParticleScale));
    this.createDamagePool(Math.round(10 * qualityPreset.vfxParticleScale));
    this.createShieldPool(Math.max(4, Math.round(8 * qualityPreset.vfxParticleScale)));
    // Crits are rare relative to normal damage ticks, but pool generously so
    // a chained burst (e.g. Sniper Quad-shot rolling 4 crits in 200ms) never
    // overruns and clobbers a still-fading earlier flash.
    this.createCritPool(Math.max(4, Math.round(6 * qualityPreset.vfxParticleScale)));
    this.createBurstPool(Math.round(10 * qualityPreset.vfxParticleScale));
    this.createGlitchPool(3);
    this.createPuffPool(Math.round(10 * qualityPreset.vfxParticleScale));
    // ~40 sparkle particles per pickup, ~12 spark particles per hit (per spec).
    this.sparkleParticleCount = Math.max(20, Math.round(40 * qualityPreset.vfxParticleScale));
    this.hitSparkParticleCount = Math.max(8, Math.round(12 * qualityPreset.vfxParticleScale));
    // Pickups are rare (~6 concurrent), hits are frequent (~12 concurrent).
    // Floor of 1 protects against pathological quality presets.
    this.createSparklePool(Math.max(1, Math.round(6 * qualityPreset.vfxParticleScale)));
    this.createHitSparkPool(Math.max(1, Math.round(12 * qualityPreset.vfxParticleScale)));

    // === U5: knockback polish ===
    // 12 particles per heavy-hit dust burst (per spec). Heavy hits are frequent
    // during teamfights so the pool size matches hit-spark; quality scaling is
    // multiplicative to stay within the same budget envelope.
    this.knockbackDustParticleCount = Math.max(6, Math.round(12 * qualityPreset.vfxParticleScale));
    this.createKnockbackDustPool(Math.max(1, Math.round(8 * qualityPreset.vfxParticleScale)));

    // === U5: impact VFX ===
    // 8–12 debris particles per hit (per spec). Debris is fired on every
    // damage tick so the pool is sized to absorb the same burst load as
    // hit-spark without overrunning. Shield shimmer + directional flash are
    // rare-ish (1 per shield break or 1 per attacker-known damage tick),
    // so smaller pools are fine.
    this.impactDebrisParticleCount = Math.max(8, Math.round(10 * qualityPreset.vfxParticleScale));
    this.createImpactDebrisPool(Math.max(1, Math.round(12 * qualityPreset.vfxParticleScale)));
    this.createShieldShimmerPool(Math.max(1, Math.round(6 * qualityPreset.vfxParticleScale)));
    this.createDirectionalFlashPool(Math.max(1, Math.round(10 * qualityPreset.vfxParticleScale)));

    // === U7: formation-burst ===
    // Owned by EffectsManager so its update/dispose hook into the same frame
    // loop other effects already use.
    this.formationBurst = new FormationActivationBurst(scene);
  }

  /** Test-only inspector. Returns a snapshot of currently-active heal effects
   * (variant + color hex). Used by EffectsManager.test.ts to assert that
   * self-heal vs ally-heal produce distinguishable aura entries. */
  inspectActiveHealEffects(): Array<{ variant: 'ally' | 'self'; color: number }> {
    const snapshot: Array<{ variant: 'ally' | 'self'; color: number }> = [];
    for (const effect of this.healPool) {
      if (!effect.active) continue;
      snapshot.push({
        variant: effect.variant ?? 'ally',
        color: effect.uColor?.value.getHex() ?? 0,
      });
    }
    return snapshot;
  }

  spawnExplosion(position: Vec3, color: number = 0xff5a1f, attackDir?: Vec3): void {
    // Pair every drone-destruction explosion with a smoke puff. Sole call site
    // (GameClient ServerEvent.DroneDestroyed) keeps this safe to chain.
    this.playDestructionPuff(position);
    const effect = this.acquireExplosion();
    effect.active = true;
    effect.life = 0;
    effect.maxLife = 0.55;
    effect.obj.visible = true;
    effect.uColor.value.set(color);
    effect.uOpacity.value = 1;
    effect.uProgress.value = 0;

    let aDirX = 0, aDirY = 0, aDirZ = 0;
    if (attackDir) {
      const len = Math.sqrt(attackDir.x ** 2 + attackDir.y ** 2 + attackDir.z ** 2);
      if (len > 0.01) {
        aDirX = attackDir.x / len;
        aDirY = attackDir.y / len;
        aDirZ = attackDir.z / len;
      }
    }

    for (let i = 0; i < this.explosionParticleCount; i++) {
      const i3 = i * 3;
      effect.positions[i3] = position.x;
      effect.positions[i3 + 1] = position.y;
      effect.positions[i3 + 2] = position.z;

      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;
      const speed = 3.5 + Math.random() * 6.5;
      effect.velocities[i3] = Math.sin(phi) * Math.cos(theta) * speed + aDirX * 3.0;
      effect.velocities[i3 + 1] = Math.cos(phi) * speed + aDirY * 3.0;
      effect.velocities[i3 + 2] = Math.sin(phi) * Math.sin(theta) * speed + aDirZ * 3.0;
    }

    effect.geometry.attributes.position.needsUpdate = true;
  }

  /**
   * Smoke-and-debris puff layered with {@link spawnExplosion} on drone death.
   * Slower outward spread than the fireball, with a small upward bias to read
   * as a pressure wave before gravity settles the cloud over ~1.5s.
   */
  playDestructionPuff(position: Vec3): void {
    const effect = this.acquirePuff();
    effect.active = true;
    effect.life = 0;
    effect.maxLife = 1.5;
    effect.obj.visible = true;
    effect.uOpacity.value = 1;
    effect.uProgress.value = 0;

    for (let i = 0; i < this.puffParticleCount; i++) {
      const i3 = i * 3;
      effect.positions[i3] = position.x;
      effect.positions[i3 + 1] = position.y;
      effect.positions[i3 + 2] = position.z;

      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;
      const speed = 1.2 + Math.random() * 1.6;
      effect.velocities[i3] = Math.sin(phi) * Math.cos(theta) * speed;
      effect.velocities[i3 + 1] = Math.cos(phi) * speed + 0.6;
      effect.velocities[i3 + 2] = Math.sin(phi) * Math.sin(theta) * speed;
    }

    effect.geometry.attributes.position.needsUpdate = true;
  }

  showHealAura(position: Vec3, radius: number): void {
    const effect = this.acquireRing(this.healPool, 'heal');
    effect.active = true;
    effect.life = 0;
    effect.maxLife = 0.7;
    effect.obj.visible = true;
    effect.obj.position.set(position.x, position.y, position.z);
    effect.obj.scale.set(radius, radius, radius);
    effect.uProgress.value = 0;
    effect.variant = 'ally';
    effect.uColor?.value.set(HEAL_AURA_COLOR_ALLY);
  }

  /**
   * Distinct variant of {@link showHealAura} for the local Healer's self-heal
   * passive ({@link DroneType.Healer} `healer_aura`). Same lifecycle/cleanup
   * as the ally aura — only the color uniform and the initial scale change so
   * the player can tell their own regen apart from ally heals in heavy combat.
   */
  showSelfHealAura(position: Vec3, radius: number): void {
    const effect = this.acquireRing(this.healPool, 'heal');
    effect.active = true;
    effect.life = 0;
    effect.maxLife = 0.85;
    effect.obj.visible = true;
    effect.obj.position.set(position.x, position.y, position.z);
    const r = radius * 1.35;
    effect.obj.scale.set(r, r, r);
    effect.uProgress.value = 0;
    effect.variant = 'self';
    effect.uColor?.value.set(HEAL_AURA_COLOR_SELF);
  }

  showDamageFlash(position: Vec3, impactDir?: Vec3): void {
    const effect = this.acquireRing(this.damagePool, 'damage');
    effect.active = true;
    effect.life = 0;
    effect.maxLife = 0.24;
    effect.obj.visible = true;
    effect.obj.position.set(position.x, position.y, position.z);
    effect.obj.scale.setScalar(1);
    effect.uProgress.value = 0;
    effect.uImpactDir?.value.set(impactDir?.x ?? 0, impactDir?.y ?? 0, impactDir?.z ?? 0);
  }

  /**
   * Crit hit flash. A brief golden-orange ring at the impact point (220ms
   * life) plus a screen-corner flicker overlay so the crit reads as more
   * than just a re-tinted damage tick. Layered on top of the standard
   * {@link showDamageFlash} (the normal damage ring still fires) — this
   * is purely additive feedback for the crit beat.
   */
  showCritFlash(position: Vec3): void {
    const effect = this.acquireCritRing();
    effect.active = true;
    effect.life = 0;
    effect.maxLife = 0.22;
    effect.obj.visible = true;
    effect.obj.position.set(position.x, position.y, position.z);
    effect.obj.scale.setScalar(1);
    effect.uProgress.value = 0;
    this.triggerCritOverlay();
  }

  /** Pulse the screen-corner overlay (alpha-fades a fullscreen DOM div)
   *  for ~220ms. No-ops in non-DOM environments so unit tests that never
   *  touch a document still pass. */
  private triggerCritOverlay(): void {
    if (typeof document === 'undefined') return;
    if (!this.critOverlay) {
      const div = document.createElement('div');
      div.className = 'crit-flash-overlay';
      div.setAttribute('aria-hidden', 'true');
      // Pointer events off so the overlay never steals clicks from HUD.
      div.style.pointerEvents = 'none';
      document.body.appendChild(div);
      this.critOverlay = div;
    }
    // Restart the animation by clearing the active class, forcing reflow,
    // then re-adding. This guarantees back-to-back crits don't merge into
    // one continuous flicker.
    const div = this.critOverlay;
    div.classList.remove('active');
    // Reading offsetWidth forces a synchronous layout, restarting the CSS
    // animation when we re-add the class on the next line.
    void div.offsetWidth;
    div.classList.add('active');
    if (this.critOverlayTimer !== null) clearTimeout(this.critOverlayTimer);
    this.critOverlayTimer = setTimeout(() => {
      div.classList.remove('active');
      this.critOverlayTimer = null;
    }, 240);
  }

  /**
   * White expanding-ring flash on a Shield Pulse absorb. Shorter-lived than
   * {@link showDamageFlash} (~280ms) so back-to-back saves don't overlap into
   * a constant glow, and uses the dedicated white shield pool so the player
   * can read "free save" instead of "took damage".
   */
  showShieldBreak(position: Vec3): void {
    const effect = this.acquireRing(this.shieldPool, 'shield');
    effect.active = true;
    effect.life = 0;
    effect.maxLife = 0.28;
    effect.obj.visible = true;
    effect.obj.position.set(position.x, position.y, position.z);
    effect.obj.scale.setScalar(1);
    effect.uProgress.value = 0;
  }

  /**
   * Particle fan + ground ring at the swarm centroid on a real card
   * activation. Color is routed through CARD_ACTIVATION_COLORS; unknown
   * card types fall back to the same debug magenta as showCardActivation.
   */
  showFormationBurst(centroid: Vec3, cardType: number): void {
    const color = CARD_ACTIVATION_COLORS[cardType];
    if (color === undefined) {
      console.warn(`[EffectsManager] no formation-burst color for type ${cardType}`);
    }
    this.formationBurst.spawn(centroid, color ?? 0xff00ff);
  }

  /**
   * Spawn a card-type-specific signature effect at the given position.
   * Additive on top of `showFormationBurst`/`showCardActivation`; unknown
   * card types are silently ignored (the generic burst still plays).
   *
   * `payload` carries optional extras some signatures need:
   *   - target: target position (Sniper laser endpoint)
   *   - allies: ally drone positions (Support buff aura)
   *   - liveResolver: per-tick resolver returning the caster's current
   *     centroid so the FX rides along with a moving caster instead of
   *     staying pinned to the snapshot taken at activation
   *   - targetResolver: same idea for the target endpoint (Sniper laser)
   */
  spawnCardSignature(
    cardType: number,
    position: Vec3,
    payload?: {
      target?: Vec3;
      allies?: Vec3[];
      liveResolver?: LivePositionResolver;
      targetResolver?: LivePositionResolver;
    },
  ): void {
    const liveResolver = payload?.liveResolver;
    const targetResolver = payload?.targetResolver;
    let effect:
      | { update(deltaMs: number): boolean; dispose(): void }
      | null = null;
    switch (cardType) {
      case AlgorithmCardType.DefensiveWall: {
        // Guardian-style taunt aura: read as "fire here, draw aggro".
        effect = new GuardianTauntAura(this.scene, position, { liveResolver });
        break;
      }
      case AlgorithmCardType.FocusFire: {
        // Sniper reticle laser: caster → focused target.
        const target = payload?.target ?? position;
        effect = new SniperReticleLaser(this.scene, position, target, {
          fromResolver: liveResolver,
          toResolver: targetResolver,
        });
        break;
      }
      case AlgorithmCardType.RelayNet:
      case AlgorithmCardType.RadarRing: {
        // Healer beacon ring at the centroid.
        effect = new HealerBeaconRing(this.scene, position, { liveResolver });
        break;
      }
      case AlgorithmCardType.Regroup:
      case AlgorithmCardType.Follow: {
        // Support buff aura tied to ally drone positions within 18u.
        const allies = payload?.allies ?? [];
        effect = new SupportBuffAura(this.scene, position, allies, { liveResolver });
        break;
      }
      case AlgorithmCardType.SyncDive:
      case AlgorithmCardType.CageSurround: {
        // Interceptor dash trail: from caster centroid toward target/payload.
        const target = payload?.target ?? position;
        effect = new InterceptorDashTrail(this.scene, position, target, { liveResolver });
        break;
      }
      case AlgorithmCardType.HorizontalOrbit: {
        // Spinning horizontal disc at the swarm centroid.
        effect = new OrbitRing(this.scene, position, { tilt: 'horizontal', liveResolver });
        break;
      }
      case AlgorithmCardType.DiagonalOrbit: {
        // Spinning diagonal disc at the swarm centroid.
        effect = new OrbitRing(this.scene, position, {
          tilt: 'diagonal',
          color: 0xb86bff,
          liveResolver,
        });
        break;
      }
      case AlgorithmCardType.Orbit: {
        // Generic orbit signature — blue tint.
        effect = new OrbitRing(this.scene, position, {
          tilt: 'horizontal',
          color: 0x4db4ff,
          liveResolver,
        });
        break;
      }
      case AlgorithmCardType.ChoreoUltimate: {
        // Large radial pulse at the swarm centroid.
        effect = new ChoreoBurst(this.scene, position, { liveResolver });
        break;
      }
      case AlgorithmCardType.ConvertShot: {
        // Crosshair flash on the focused target (or centroid if none).
        const target = payload?.target ?? position;
        effect = new ConvertShotFlash(this.scene, target, {
          liveResolver: targetResolver,
        });
        break;
      }
      case AlgorithmCardType.Scatter: {
        // Spread-out particle splash from the centroid.
        effect = new BrScatterSplash(this.scene, position, { liveResolver });
        break;
      }
      default:
        // Unknown / no signature for this card — fall through to generic burst.
        return;
    }
    if (effect) {
      this.cardSignatures.push(effect);
    }
  }

  showCardActivation(position: Vec3, cardType: number): void {
    const effect = this.acquireBurst();
    effect.active = true;
    effect.life = 0;
    effect.maxLife = 0.65;
    effect.obj.visible = true;
    effect.uProgress.value = 0;
    const color = CARD_ACTIVATION_COLORS[cardType];
    if (color === undefined) {
      console.warn(`[EffectsManager] no card color for type ${cardType}`);
    }
    effect.uColor.value.set(color ?? 0xff00ff);

    for (let i = 0; i < effect.positions.length / 3; i++) {
      const i3 = i * 3;
      effect.positions[i3] = position.x;
      effect.positions[i3 + 1] = position.y;
      effect.positions[i3 + 2] = position.z;

      const theta = (i / (effect.positions.length / 3)) * Math.PI * 2;
      const speed = 2.2 + Math.random() * 2.1;
      effect.velocities[i3] = Math.cos(theta) * speed;
      effect.velocities[i3 + 1] = (Math.random() - 0.3) * 1.4;
      effect.velocities[i3 + 2] = Math.sin(theta) * speed;
    }

    effect.geometry.attributes.position.needsUpdate = true;
  }

  /**
   * Pickup sparkle: vertical-bias particle burst tinted by pickup type.
   * Drone pickups read cyan, Algorithm pickups gold, so the player can tell
   * what they grabbed at a glance even off-center on screen.
   */
  showPickupSparkle(position: Vec3, pickupType: number): void {
    const effect = this.acquireSparkle();
    effect.active = true;
    effect.life = 0;
    effect.maxLife = 0.6;
    effect.obj.visible = true;
    effect.uProgress.value = 0;
    const color = PICKUP_SPARKLE_COLORS[pickupType];
    if (color === undefined) {
      console.warn(`[EffectsManager] no pickup color for type ${pickupType}`);
    }
    effect.uColor.value.set(color ?? 0xff00ff);

    for (let i = 0; i < this.sparkleParticleCount; i++) {
      const i3 = i * 3;
      effect.positions[i3] = position.x;
      effect.positions[i3 + 1] = position.y;
      effect.positions[i3 + 2] = position.z;

      // Vertical bias: Y velocity dominates over horizontal so particles
      // shoot up like a small fountain, then drift outward as they slow.
      const theta = Math.random() * Math.PI * 2;
      const horizSpeed = 1.0 + Math.random() * 1.4;
      const vertSpeed = 3.5 + Math.random() * 3.0;
      effect.velocities[i3] = Math.cos(theta) * horizSpeed;
      effect.velocities[i3 + 1] = vertSpeed;
      effect.velocities[i3 + 2] = Math.sin(theta) * horizSpeed;
    }

    effect.geometry.attributes.position.needsUpdate = true;
  }

  /**
   * Hit spark: small bright particles at the impact point. If `direction` is
   * provided (the unit vector from attacker → target), particles fly back
   * along the reverse direction (i.e. "splash back" toward the shooter).
   * Otherwise an isotropic burst.
   */
  showHitSpark(position: Vec3, direction?: Vec3): void {
    const effect = this.acquireHitSpark();
    effect.active = true;
    effect.life = 0;
    effect.maxLife = 0.18;
    effect.obj.visible = true;
    effect.uProgress.value = 0;

    let backX = 0, backY = 0, backZ = 0;
    let biased = false;
    if (direction) {
      const len = Math.sqrt(direction.x ** 2 + direction.y ** 2 + direction.z ** 2);
      if (len > 0.01) {
        backX = -direction.x / len;
        backY = -direction.y / len;
        backZ = -direction.z / len;
        biased = true;
      }
    }

    for (let i = 0; i < this.hitSparkParticleCount; i++) {
      const i3 = i * 3;
      effect.positions[i3] = position.x;
      effect.positions[i3 + 1] = position.y;
      effect.positions[i3 + 2] = position.z;

      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;
      const speed = 4.0 + Math.random() * 4.0;
      const vx = Math.sin(phi) * Math.cos(theta) * speed;
      const vy = Math.cos(phi) * speed;
      const vz = Math.sin(phi) * Math.sin(theta) * speed;
      // When direction is known, bias 70% along the back vector so the spark
      // visibly fans away from the shot path; remaining 30% scatters.
      if (biased) {
        effect.velocities[i3] = vx * 0.3 + backX * speed * 0.7;
        effect.velocities[i3 + 1] = vy * 0.3 + backY * speed * 0.7;
        effect.velocities[i3 + 2] = vz * 0.3 + backZ * speed * 0.7;
      } else {
        effect.velocities[i3] = vx;
        effect.velocities[i3 + 1] = vy;
        effect.velocities[i3 + 2] = vz;
      }
    }

    effect.geometry.attributes.position.needsUpdate = true;
  }

  /**
   * Radial dust burst at a heavy-hit impact point. ~12 particles fan out
   * along {@link dir} (the unit attacker→victim vector) with a slight cone
   * spread; gravity pulls them down, drag slows them; full life ~400ms.
   * Reuses the round-robin pool pattern of {@link showHitSpark} so back-to-back
   * heavy hits don't stutter.
   */
  showKnockbackDust(position: Vec3, dir: Vec3): void {
    const effect = this.acquireKnockbackDust();
    effect.active = true;
    effect.life = 0;
    effect.maxLife = 0.4;
    effect.obj.visible = true;
    effect.uProgress.value = 0;

    let dx = 0, dy = 0, dz = 0;
    const len = Math.sqrt(dir.x ** 2 + dir.y ** 2 + dir.z ** 2);
    if (len > 0.01) {
      dx = dir.x / len;
      dy = dir.y / len;
      dz = dir.z / len;
    }

    for (let i = 0; i < this.knockbackDustParticleCount; i++) {
      const i3 = i * 3;
      // Spawn at the impact, lifted slightly so the dust doesn't clip below
      // the drone hull on flat ground.
      effect.positions[i3] = position.x;
      effect.positions[i3 + 1] = position.y;
      effect.positions[i3 + 2] = position.z;

      // Cone spread around the knockback direction: bias 70% along `dir`,
      // 30% randomized so the burst reads as a fan rather than a beam.
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;
      const speed = 3.5 + Math.random() * 2.5;
      const rx = Math.sin(phi) * Math.cos(theta);
      const ry = Math.cos(phi);
      const rz = Math.sin(phi) * Math.sin(theta);
      effect.velocities[i3]     = (dx * 0.7 + rx * 0.3) * speed;
      effect.velocities[i3 + 1] = (dy * 0.4 + ry * 0.3 + 0.4) * speed;
      effect.velocities[i3 + 2] = (dz * 0.7 + rz * 0.3) * speed;
    }

    effect.geometry.attributes.position.needsUpdate = true;
  }

  /**
   * Hit-position debris burst at the impact point. Fires on every damage
   * tick — different from {@link showHitSpark} (which is white→yellow and
   * splashes back along the shot path) and from {@link showKnockbackDust}
   * (which is dust kicked along the knockback vector and is only rendered
   * on heavy hits). Debris reads as "metal shavings off the hit" and is
   * tinted by `isCrit` so crits get a brighter white-yellow burst.
   *
   * @param position World-space impact point.
   * @param attackerToTarget Optional unit vector from attacker → target.
   *   When provided, the hemisphere of debris is oriented in this direction
   *   so the spray "comes from" the impact face.
   * @param isCrit Use the white-yellow crit color when true.
   */
  spawnImpactDebris(
    position: Vec3,
    attackerToTarget?: Vec3,
    isCrit: boolean = false,
  ): void {
    const effect = this.acquireImpactDebris();
    effect.active = true;
    effect.life = 0;
    effect.maxLife = 0.25;
    effect.obj.visible = true;
    effect.uProgress.value = 0;
    // Crit tint shifts toward white-yellow; normal hits use orange-yellow.
    effect.uColor.value.set(isCrit ? 0xfff5a0 : 0xff9438);

    let dx = 0, dy = 1, dz = 0; // default fan direction = up if no attacker
    if (attackerToTarget) {
      const len = Math.sqrt(
        attackerToTarget.x ** 2 + attackerToTarget.y ** 2 + attackerToTarget.z ** 2,
      );
      if (len > 0.01) {
        dx = attackerToTarget.x / len;
        dy = attackerToTarget.y / len;
        dz = attackerToTarget.z / len;
      }
    }

    // Build an orthonormal basis around dir once per call — the basis only
    // depends on the attacker→target direction, not on the per-particle
    // angles, so hoisting it out of the loop saves N cross-products + sqrts.
    const upX = Math.abs(dy) < 0.99 ? 0 : 1;
    const upY = Math.abs(dy) < 0.99 ? 1 : 0;
    const upZ = 0;
    const tx = upY * dz - upZ * dy;
    const ty = upZ * dx - upX * dz;
    const tz = upX * dy - upY * dx;
    const tlen = Math.sqrt(tx * tx + ty * ty + tz * tz) || 1;
    const t1x = tx / tlen, t1y = ty / tlen, t1z = tz / tlen;
    const t2x = dy * t1z - dz * t1y;
    const t2y = dz * t1x - dx * t1z;
    const t2z = dx * t1y - dy * t1x;

    for (let i = 0; i < this.impactDebrisParticleCount; i++) {
      const i3 = i * 3;
      effect.positions[i3] = position.x;
      effect.positions[i3 + 1] = position.y;
      effect.positions[i3 + 2] = position.z;

      // Hemisphere fan oriented along attacker→target so the burst sprays
      // "outward from the impact face" rather than isotropically.
      const theta = Math.random() * Math.PI * 2;
      // Bias the cone tightly around dir (cos(phi) in [0.4, 1.0]) so the
      // bulk of the spray is forward, but still has some spread.
      const cosPhi = 0.4 + Math.random() * 0.6;
      const sinPhi = Math.sqrt(Math.max(0, 1 - cosPhi * cosPhi));
      const speed = 5.0 + Math.random() * 4.0;

      const sx = sinPhi * Math.cos(theta);
      const sy = sinPhi * Math.sin(theta);
      const vxDir = dx * cosPhi + t1x * sx + t2x * sy;
      const vyDir = dy * cosPhi + t1y * sx + t2y * sy;
      const vzDir = dz * cosPhi + t1z * sx + t2z * sy;

      effect.velocities[i3] = vxDir * speed;
      effect.velocities[i3 + 1] = vyDir * speed + 1.0; // small upward kick
      effect.velocities[i3 + 2] = vzDir * speed;
    }

    effect.geometry.attributes.position.needsUpdate = true;
  }

  /**
   * Layered second pass on top of the cyan {@link showShieldBreak} ring.
   * Fires a faster-expanding cyan→white shimmer with a chromatic-aberration
   * edge so back-to-back shield saves still read as discrete events. Total
   * combined lifetime ~600ms (cyan ring 280ms + shimmer 320ms overlap).
   */
  showShieldBreakShimmer(position: Vec3): void {
    const effect = this.acquireShieldShimmer();
    effect.active = true;
    effect.life = 0;
    effect.maxLife = 0.32;
    effect.obj.visible = true;
    effect.obj.position.set(position.x, position.y, position.z);
    effect.obj.scale.setScalar(1);
    effect.uProgress.value = 0;
  }

  /**
   * Directional damage flash. Layered on top of the symmetric
   * {@link showDamageFlash} when an attacker position is known so the ring
   * is brightest on the impact-facing hemisphere. The biased side reads as
   * "the hit came from there" without obscuring the existing flash.
   */
  showDirectionalDamageFlash(position: Vec3, attackerToTarget: Vec3): void {
    const effect = this.acquireDirectionalFlash();
    effect.active = true;
    effect.life = 0;
    effect.maxLife = 0.24;
    effect.obj.visible = true;
    effect.obj.position.set(position.x, position.y, position.z);
    effect.obj.scale.setScalar(1);
    effect.uProgress.value = 0;
    // Reverse: the impact-direction vector points FROM the attacker TO the
    // target, so the bright side of the ring should face attacker → i.e. the
    // negative of attackerToTarget. We pass the reversed vector to the shader
    // so it can use a positive dot product on the front hemisphere.
    effect.uImpactDir.value.set(
      -attackerToTarget.x,
      -attackerToTarget.y,
      -attackerToTarget.z,
    );
  }

  showMalfunction(position: Vec3): void {
    this.playGlitch(position, 0.18);
  }

  /**
   * ConvertShot impact: a longer-lived, more pronounced version of the
   * malfunction glitch (~600ms vs ~180ms). The lingering shimmer + VHS pulse
   * sells the "system hijacked" beat that was previously silent.
   */
  spawnGlitchAt(position: Vec3): void {
    this.playGlitch(position, 0.6);
  }

  private playGlitch(position: Vec3, maxLife: number): void {
    const effect = this.acquireGlitch();
    effect.active = true;
    effect.life = 0;
    effect.maxLife = maxLife;
    effect.obj.visible = true;
    effect.obj.position.set(position.x, position.y, position.z);
    effect.uProgress.value = 0;
    if (effect.uSeed) {
      effect.uSeed.value = Math.random() * 100;
    }
  }

  update(deltaMs: number): void {
    const dt = deltaMs / 1000;

    for (const effect of this.explosionPool) {
      if (!effect.active) continue;
      effect.life += dt;
      const t = effect.life / effect.maxLife;
      if (t >= 1) {
        this.deactivateExplosion(effect);
        continue;
      }

      const posAttr = effect.geometry.attributes.position as THREE.BufferAttribute;
      const drag = 1 - t * 0.8;
      for (let i = 0; i < this.explosionParticleCount; i++) {
        const i3 = i * 3;
        effect.velocities[i3 + 1] -= 9.8 * dt;
        posAttr.setXYZ(
          i,
          posAttr.getX(i) + effect.velocities[i3] * dt * drag,
          posAttr.getY(i) + effect.velocities[i3 + 1] * dt * drag,
          posAttr.getZ(i) + effect.velocities[i3 + 2] * dt * drag,
        );
      }
      posAttr.needsUpdate = true;
      effect.uProgress.value = t;
      effect.uOpacity.value = 1 - t;
    }

    // Calibrated to match the previous 1.01-per-frame multiplier at 60Hz
    // (1.01^60 ≈ 1.82). dt-based so the visual is identical at 30/144Hz.
    const HEAL_AURA_GROWTH_PER_SEC = 0.6;
    for (const effect of this.healPool) {
      if (!effect.active) continue;
      effect.life += dt;
      const t = effect.life / effect.maxLife;
      if (t >= 1) {
        this.deactivateRing(effect);
        continue;
      }

      effect.obj.scale.setScalar(effect.obj.scale.x * (1 + HEAL_AURA_GROWTH_PER_SEC * dt));
      effect.uProgress.value = t;
    }

    for (const effect of this.damagePool) {
      if (!effect.active) continue;
      effect.life += dt;
      const t = effect.life / effect.maxLife;
      if (t >= 1) {
        this.deactivateRing(effect);
        continue;
      }

      const scale = 1 + t * 1.8;
      effect.obj.scale.set(scale, scale, scale);
      effect.uProgress.value = t;
    }

    for (const effect of this.shieldPool) {
      if (!effect.active) continue;
      effect.life += dt;
      const t = effect.life / effect.maxLife;
      if (t >= 1) {
        this.deactivateRing(effect);
        continue;
      }
      // Slightly snappier expansion than damage flash so the white ring
      // resolves quickly and reads as a discrete "save" not a glow.
      const scale = 1 + t * 2.2;
      effect.obj.scale.set(scale, scale, scale);
      effect.uProgress.value = t;
    }

    // Crit ring — fast outward expansion (faster than damage / shield) so
    // the gold flash resolves cleanly inside the ~220ms window.
    for (const effect of this.critPool) {
      if (!effect.active) continue;
      effect.life += dt;
      const t = effect.life / effect.maxLife;
      if (t >= 1) {
        this.deactivateRing(effect);
        continue;
      }
      const scale = 1 + t * 3.4;
      effect.obj.scale.set(scale, scale, scale);
      effect.uProgress.value = t;
    }

    for (const effect of this.burstPool) {
      if (!effect.active) continue;
      effect.life += dt;
      const t = effect.life / effect.maxLife;
      if (t >= 1) {
        this.deactivateBurst(effect);
        continue;
      }

      const posAttr = effect.geometry.attributes.position as THREE.BufferAttribute;
      const drag = 1 - t * 0.9;
      for (let i = 0; i < effect.positions.length / 3; i++) {
        const i3 = i * 3;
        posAttr.setXYZ(
          i,
          posAttr.getX(i) + effect.velocities[i3] * dt * drag,
          posAttr.getY(i) + effect.velocities[i3 + 1] * dt * drag,
          posAttr.getZ(i) + effect.velocities[i3 + 2] * dt * drag,
        );
      }
      posAttr.needsUpdate = true;
      effect.uProgress.value = t;
    }

    for (const effect of this.glitchPool) {
      if (!effect.active) continue;
      effect.life += dt;
      const t = effect.life / effect.maxLife;
      if (t >= 1) {
        this.deactivateRing(effect);
        continue;
      }
      effect.uProgress.value = t;
    }

    // Smoke puff — half-gravity and slower drag than the explosion so the
    // cloud reads as smoke settling below the fireball.
    for (const effect of this.puffPool) {
      if (!effect.active) continue;
      effect.life += dt;
      const t = effect.life / effect.maxLife;
      if (t >= 1) {
        this.deactivatePuff(effect);
        continue;
      }

      const posAttr = effect.geometry.attributes.position as THREE.BufferAttribute;
      const drag = 1 - t * 0.6;
      for (let i = 0; i < this.puffParticleCount; i++) {
        const i3 = i * 3;
        effect.velocities[i3 + 1] -= 4.9 * dt;
        posAttr.setXYZ(
          i,
          posAttr.getX(i) + effect.velocities[i3] * dt * drag,
          posAttr.getY(i) + effect.velocities[i3 + 1] * dt * drag,
          posAttr.getZ(i) + effect.velocities[i3 + 2] * dt * drag,
        );
      }
      posAttr.needsUpdate = true;
      effect.uProgress.value = t;
      effect.uOpacity.value = 1 - t;
    }

    // Sparkle — light gravity (~3.5 m/s²) so particles arc gently over
    // their 0.6s life. Drag is mild for a flowing fountain feel.
    for (const effect of this.sparklePool) {
      if (!effect.active) continue;
      effect.life += dt;
      const t = effect.life / effect.maxLife;
      if (t >= 1) {
        this.deactivateSparkle(effect);
        continue;
      }

      const posAttr = effect.geometry.attributes.position as THREE.BufferAttribute;
      const drag = 1 - t * 0.5;
      for (let i = 0; i < this.sparkleParticleCount; i++) {
        const i3 = i * 3;
        effect.velocities[i3 + 1] -= 3.5 * dt;
        posAttr.setXYZ(
          i,
          posAttr.getX(i) + effect.velocities[i3] * dt * drag,
          posAttr.getY(i) + effect.velocities[i3 + 1] * dt * drag,
          posAttr.getZ(i) + effect.velocities[i3 + 2] * dt * drag,
        );
      }
      posAttr.needsUpdate = true;
      effect.uProgress.value = t;
    }

    this.formationBurst.update(deltaMs);

    // Pump per-card signatures; remove finished entries in-place.
    for (let i = this.cardSignatures.length - 1; i >= 0; i--) {
      const alive = this.cardSignatures[i].update(deltaMs);
      if (!alive) {
        this.cardSignatures.splice(i, 1);
      }
    }

    // Hit spark — no gravity per spec. Strong drag so particles decelerate
    // and the burst fizzles in <200ms without leaving stragglers.
    for (const effect of this.hitSparkPool) {
      if (!effect.active) continue;
      effect.life += dt;
      const t = effect.life / effect.maxLife;
      if (t >= 1) {
        this.deactivateHitSpark(effect);
        continue;
      }

      const posAttr = effect.geometry.attributes.position as THREE.BufferAttribute;
      const drag = 1 - t * 0.85;
      for (let i = 0; i < this.hitSparkParticleCount; i++) {
        const i3 = i * 3;
        posAttr.setXYZ(
          i,
          posAttr.getX(i) + effect.velocities[i3] * dt * drag,
          posAttr.getY(i) + effect.velocities[i3 + 1] * dt * drag,
          posAttr.getZ(i) + effect.velocities[i3 + 2] * dt * drag,
        );
      }
      posAttr.needsUpdate = true;
      effect.uProgress.value = t;
    }

    // Knockback dust — gentle gravity and moderate drag so particles arc
    // forward then settle. Shorter life (~400ms) than the destruction puff
    // so multiple heavy hits don't pile into a wall of dust.
    for (const effect of this.knockbackDustPool) {
      if (!effect.active) continue;
      effect.life += dt;
      const t = effect.life / effect.maxLife;
      if (t >= 1) {
        this.deactivateKnockbackDust(effect);
        continue;
      }

      const posAttr = effect.geometry.attributes.position as THREE.BufferAttribute;
      const drag = 1 - t * 0.7;
      for (let i = 0; i < this.knockbackDustParticleCount; i++) {
        const i3 = i * 3;
        effect.velocities[i3 + 1] -= 6.5 * dt;
        posAttr.setXYZ(
          i,
          posAttr.getX(i) + effect.velocities[i3] * dt * drag,
          posAttr.getY(i) + effect.velocities[i3 + 1] * dt * drag,
          posAttr.getZ(i) + effect.velocities[i3 + 2] * dt * drag,
        );
      }
      posAttr.needsUpdate = true;
      effect.uProgress.value = t;
    }

    // Impact debris — moderate gravity (gravity-tinted descent per spec) and
    // mild drag so particles arc out and down inside their 250ms life.
    for (const effect of this.impactDebrisPool) {
      if (!effect.active) continue;
      effect.life += dt;
      const t = effect.life / effect.maxLife;
      if (t >= 1) {
        this.deactivateImpactDebris(effect);
        continue;
      }

      const posAttr = effect.geometry.attributes.position as THREE.BufferAttribute;
      const drag = 1 - t * 0.6;
      for (let i = 0; i < this.impactDebrisParticleCount; i++) {
        const i3 = i * 3;
        effect.velocities[i3 + 1] -= 9.0 * dt;
        posAttr.setXYZ(
          i,
          posAttr.getX(i) + effect.velocities[i3] * dt * drag,
          posAttr.getY(i) + effect.velocities[i3 + 1] * dt * drag,
          posAttr.getZ(i) + effect.velocities[i3 + 2] * dt * drag,
        );
      }
      posAttr.needsUpdate = true;
      effect.uProgress.value = t;
    }

    // Shield-break shimmer — second pass, expands faster than the cyan ring
    // so the chromatic edge resolves before the cyan ring finishes fading.
    for (const effect of this.shieldShimmerPool) {
      if (!effect.active) continue;
      effect.life += dt;
      const t = effect.life / effect.maxLife;
      if (t >= 1) {
        this.deactivateShieldShimmer(effect);
        continue;
      }
      const scale = 1 + t * 2.6;
      effect.obj.scale.set(scale, scale, scale);
      effect.uProgress.value = t;
    }

    // Directional damage flash — same expansion curve as the symmetric flash.
    for (const effect of this.directionalFlashPool) {
      if (!effect.active) continue;
      effect.life += dt;
      const t = effect.life / effect.maxLife;
      if (t >= 1) {
        this.deactivateDirectionalFlash(effect);
        continue;
      }
      const scale = 1 + t * 1.8;
      effect.obj.scale.set(scale, scale, scale);
      effect.uProgress.value = t;
    }
  }

  dispose(): void {
    for (const effect of this.explosionPool) {
      effect.geometry.dispose();
      effect.material.dispose();
    }
    for (const effect of this.healPool) {
      effect.obj.geometry.dispose();
      effect.material.dispose();
    }
    for (const effect of this.damagePool) {
      effect.obj.geometry.dispose();
      effect.material.dispose();
    }
    for (const effect of this.shieldPool) {
      effect.obj.geometry.dispose();
      effect.material.dispose();
    }
    for (const effect of this.critPool) {
      effect.obj.geometry.dispose();
      effect.material.dispose();
    }
    if (this.critOverlayTimer !== null) {
      clearTimeout(this.critOverlayTimer);
      this.critOverlayTimer = null;
    }
    if (this.critOverlay && this.critOverlay.parentElement) {
      this.critOverlay.parentElement.removeChild(this.critOverlay);
    }
    this.critOverlay = null;
    for (const effect of this.burstPool) {
      effect.geometry.dispose();
      effect.material.dispose();
    }
    for (const effect of this.glitchPool) {
      effect.obj.geometry.dispose();
      effect.material.dispose();
    }
    for (const effect of this.puffPool) {
      effect.geometry.dispose();
      effect.material.dispose();
    }
    for (const effect of this.sparklePool) {
      effect.geometry.dispose();
      effect.material.dispose();
    }
    for (const effect of this.hitSparkPool) {
      effect.geometry.dispose();
      effect.material.dispose();
    }
    for (const effect of this.knockbackDustPool) {
      effect.geometry.dispose();
      effect.material.dispose();
    }
    for (const effect of this.impactDebrisPool) {
      effect.geometry.dispose();
      effect.material.dispose();
    }
    for (const effect of this.shieldShimmerPool) {
      effect.obj.geometry.dispose();
      effect.material.dispose();
    }
    for (const effect of this.directionalFlashPool) {
      effect.obj.geometry.dispose();
      effect.material.dispose();
    }
    this.formationBurst.dispose();
    for (const effect of this.cardSignatures) {
      effect.dispose();
    }
    this.cardSignatures.length = 0;
  }

  private createExplosionPool(size: number): void {
    for (let i = 0; i < size; i++) {
      const positions = new Float32Array(this.explosionParticleCount * 3);
      const velocities = new Float32Array(this.explosionParticleCount * 3);
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

      const uProgress = uniform(0);
      const uOpacity = uniform(0);
      const uColor = uniform(new THREE.Color(0xff5a1f));

      const material = new THREE.PointsNodeMaterial({
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });
      const { sizeNode, colorNode } = buildExplosionNodes(uProgress, uOpacity, uColor);
      material.sizeNode = sizeNode;
      material.colorNode = colorNode;
      setEmissiveNode(material, colorNode);

      const points = new THREE.Points(geometry, material);
      points.visible = false;
      this.scene.add(points);
      this.explosionPool.push({
        obj: points,
        material,
        geometry,
        positions,
        velocities,
        uProgress,
        uOpacity,
        uColor,
        active: false,
        life: 0,
        maxLife: 0.55,
      });
    }
  }

  private createHealPool(size: number): void {
    for (let i = 0; i < size; i++) {
      const geometry = new THREE.RingGeometry(0.18, 1, 64, 1);

      const uProgress = uniform(0);
      const uColor = uniform(new THREE.Color(HEAL_AURA_COLOR_ALLY));

      const material = new THREE.MeshBasicNodeMaterial({
        transparent: true,
        depthWrite: false,
        side: THREE.DoubleSide,
        blending: THREE.AdditiveBlending,
      });
      const { colorNode } = buildHealNodes(uProgress, uColor);
      material.colorNode = colorNode;
      setEmissiveNode(material, colorNode);

      const mesh = new THREE.Mesh(geometry, material);
      mesh.rotation.x = Math.PI / 2;
      mesh.visible = false;
      this.scene.add(mesh);
      this.healPool.push({
        obj: mesh,
        material,
        uProgress,
        uColor,
        active: false,
        life: 0,
        maxLife: 0.7,
      });
    }
  }

  private createDamagePool(size: number): void {
    for (let i = 0; i < size; i++) {
      const geometry = new THREE.SphereGeometry(0.55, 12, 8);

      const uProgress = uniform(0);
      const uImpactDir = uniform(new THREE.Vector3(0, 0, 0));

      const material = new THREE.MeshBasicNodeMaterial({
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });
      const { colorNode } = buildDamageNodes(uProgress, uImpactDir);
      material.colorNode = colorNode;
      setEmissiveNode(material, colorNode);

      const mesh = new THREE.Mesh(geometry, material);
      mesh.visible = false;
      this.scene.add(mesh);
      this.damagePool.push({
        obj: mesh,
        material,
        uProgress,
        uImpactDir,
        active: false,
        life: 0,
        maxLife: 0.24,
      });
    }
  }

  private createShieldPool(size: number): void {
    for (let i = 0; i < size; i++) {
      const geometry = new THREE.SphereGeometry(0.55, 12, 8);

      const uProgress = uniform(0);

      const material = new THREE.MeshBasicNodeMaterial({
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });
      const { colorNode } = buildShieldBreakNodes(uProgress);
      material.colorNode = colorNode;
      setEmissiveNode(material, colorNode);

      const mesh = new THREE.Mesh(geometry, material);
      mesh.visible = false;
      this.scene.add(mesh);
      this.shieldPool.push({
        obj: mesh,
        material,
        uProgress,
        active: false,
        life: 0,
        maxLife: 0.28,
      });
    }
  }

  private createCritPool(size: number): void {
    for (let i = 0; i < size; i++) {
      // Flat ring oriented like the heal aura so the flash reads as a
      // ground-plane pulse around the victim drone, not a sphere.
      const geometry = new THREE.RingGeometry(0.18, 1, 48, 1);

      const uProgress = uniform(0);

      const material = new THREE.MeshBasicNodeMaterial({
        transparent: true,
        depthWrite: false,
        side: THREE.DoubleSide,
        blending: THREE.AdditiveBlending,
      });
      const { colorNode } = buildCritRingNodes(uProgress);
      material.colorNode = colorNode;
      setEmissiveNode(material, colorNode);

      const mesh = new THREE.Mesh(geometry, material);
      mesh.rotation.x = Math.PI / 2;
      mesh.visible = false;
      this.scene.add(mesh);
      this.critPool.push({
        obj: mesh,
        material,
        uProgress,
        active: false,
        life: 0,
        maxLife: 0.22,
      });
    }
  }

  /** Find the first inactive slot; if every slot is in-flight, fall back to
   *  the cursor (oldest) and bump it. Used by every acquireXxx helper so
   *  rapid bursts don't reset a still-fading earlier effect mid-pulse. */
  private acquireFromPool<T extends { active: boolean }>(
    pool: T[],
    cursor: number,
  ): { effect: T; nextCursor: number } {
    for (const eff of pool) {
      if (!eff.active) return { effect: eff, nextCursor: cursor };
    }
    return { effect: pool[cursor % pool.length], nextCursor: cursor + 1 };
  }

  private acquireCritRing(): RingEffect {
    const { effect, nextCursor } = this.acquireFromPool(this.critPool, this.critCursor);
    this.critCursor = nextCursor;
    return effect;
  }

  private createBurstPool(size: number): void {
    const burstParticleCount = 18;
    for (let i = 0; i < size; i++) {
      const positions = new Float32Array(burstParticleCount * 3);
      const velocities = new Float32Array(burstParticleCount * 3);
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

      const uProgress = uniform(0);
      const uColor = uniform(new THREE.Color(0xffffff));

      const material = new THREE.PointsNodeMaterial({
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });
      const { sizeNode, colorNode } = buildBurstNodes(uProgress, uColor);
      material.sizeNode = sizeNode;
      material.colorNode = colorNode;
      setEmissiveNode(material, colorNode);

      const points = new THREE.Points(geometry, material);
      points.visible = false;
      this.scene.add(points);
      this.burstPool.push({
        obj: points,
        material,
        geometry,
        positions,
        velocities,
        uProgress,
        uColor,
        active: false,
        life: 0,
        maxLife: 0.65,
      });
    }
  }

  private createGlitchPool(size: number): void {
    for (let i = 0; i < size; i++) {
      const geometry = new THREE.SphereGeometry(2.5, 16, 12);

      const uProgress = uniform(0);
      const uSeed = uniform(0);

      const material = new THREE.MeshBasicNodeMaterial({
        transparent: true,
        depthWrite: false,
        side: THREE.BackSide,
        blending: THREE.AdditiveBlending,
      });
      const { colorNode } = buildGlitchNodes(uProgress, uSeed);
      material.colorNode = colorNode;
      setEmissiveNode(material, colorNode);

      const mesh = new THREE.Mesh(geometry, material);
      mesh.visible = false;
      this.scene.add(mesh);
      this.glitchPool.push({
        obj: mesh,
        material,
        uProgress,
        uSeed,
        active: false,
        life: 0,
        maxLife: 0.18,
      });
    }
  }

  private acquireExplosion(): ExplosionEffect {
    const { effect, nextCursor } = this.acquireFromPool(this.explosionPool, this.explosionCursor);
    this.explosionCursor = nextCursor;
    return effect;
  }

  private acquireRing(pool: RingEffect[], kind: 'heal' | 'damage' | 'shield'): RingEffect {
    const cursor =
      kind === 'heal' ? this.healCursor : kind === 'damage' ? this.damageCursor : this.shieldCursor;
    const { effect, nextCursor } = this.acquireFromPool(pool, cursor);
    if (kind === 'heal') this.healCursor = nextCursor;
    else if (kind === 'damage') this.damageCursor = nextCursor;
    else this.shieldCursor = nextCursor;
    return effect;
  }

  private acquireBurst(): BurstEffect {
    const { effect, nextCursor } = this.acquireFromPool(this.burstPool, this.burstCursor);
    this.burstCursor = nextCursor;
    return effect;
  }

  private acquireGlitch(): RingEffect {
    const { effect, nextCursor } = this.acquireFromPool(this.glitchPool, this.glitchCursor);
    this.glitchCursor = nextCursor;
    return effect;
  }

  private deactivateExplosion(effect: ExplosionEffect): void {
    effect.active = false;
    effect.obj.visible = false;
  }

  private deactivateRing(effect: RingEffect): void {
    effect.active = false;
    effect.obj.visible = false;
  }

  private deactivateBurst(effect: BurstEffect): void {
    effect.active = false;
    effect.obj.visible = false;
  }

  private createPuffPool(size: number): void {
    for (let i = 0; i < size; i++) {
      const positions = new Float32Array(this.puffParticleCount * 3);
      const velocities = new Float32Array(this.puffParticleCount * 3);
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

      const uProgress = uniform(0);
      const uOpacity = uniform(0);

      const material = new THREE.PointsNodeMaterial({
        transparent: true,
        depthWrite: false,
        // NormalBlending (not Additive) — smoke is occluding, not luminous.
        blending: THREE.NormalBlending,
      });
      const { sizeNode, colorNode } = buildPuffNodes(uProgress, uOpacity);
      material.sizeNode = sizeNode;
      material.colorNode = colorNode;

      const points = new THREE.Points(geometry, material);
      points.visible = false;
      this.scene.add(points);
      this.puffPool.push({
        obj: points,
        material,
        geometry,
        positions,
        velocities,
        uProgress,
        uOpacity,
        active: false,
        life: 0,
        maxLife: 1.5,
      });
    }
  }

  private acquirePuff(): PuffEffect {
    const { effect, nextCursor } = this.acquireFromPool(this.puffPool, this.puffCursor);
    this.puffCursor = nextCursor;
    return effect;
  }

  private deactivatePuff(effect: PuffEffect): void {
    effect.active = false;
    effect.obj.visible = false;
  }

  private createSparklePool(size: number): void {
    for (let i = 0; i < size; i++) {
      const positions = new Float32Array(this.sparkleParticleCount * 3);
      const velocities = new Float32Array(this.sparkleParticleCount * 3);
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

      const uProgress = uniform(0);
      const uColor = uniform(new THREE.Color(0xffffff));

      const material = new THREE.PointsNodeMaterial({
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });
      const { sizeNode, colorNode } = buildSparkleNodes(uProgress, uColor);
      material.sizeNode = sizeNode;
      material.colorNode = colorNode;
      setEmissiveNode(material, colorNode);

      const points = new THREE.Points(geometry, material);
      points.visible = false;
      this.scene.add(points);
      this.sparklePool.push({
        obj: points,
        material,
        geometry,
        positions,
        velocities,
        uProgress,
        uColor,
        active: false,
        life: 0,
        maxLife: 0.6,
      });
    }
  }

  private createHitSparkPool(size: number): void {
    for (let i = 0; i < size; i++) {
      const positions = new Float32Array(this.hitSparkParticleCount * 3);
      const velocities = new Float32Array(this.hitSparkParticleCount * 3);
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

      const uProgress = uniform(0);

      const material = new THREE.PointsNodeMaterial({
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });
      const { sizeNode, colorNode } = buildHitSparkNodes(uProgress);
      material.sizeNode = sizeNode;
      material.colorNode = colorNode;
      setEmissiveNode(material, colorNode);

      const points = new THREE.Points(geometry, material);
      points.visible = false;
      this.scene.add(points);
      this.hitSparkPool.push({
        obj: points,
        material,
        geometry,
        positions,
        velocities,
        uProgress,
        active: false,
        life: 0,
        maxLife: 0.18,
      });
    }
  }

  private acquireSparkle(): SparkleEffect {
    const { effect, nextCursor } = this.acquireFromPool(this.sparklePool, this.sparkleCursor);
    this.sparkleCursor = nextCursor;
    return effect;
  }

  private acquireHitSpark(): HitSparkEffect {
    const { effect, nextCursor } = this.acquireFromPool(this.hitSparkPool, this.hitSparkCursor);
    this.hitSparkCursor = nextCursor;
    return effect;
  }

  private deactivateSparkle(effect: SparkleEffect): void {
    effect.active = false;
    effect.obj.visible = false;
  }

  private deactivateHitSpark(effect: HitSparkEffect): void {
    effect.active = false;
    effect.obj.visible = false;
  }

  private createKnockbackDustPool(size: number): void {
    for (let i = 0; i < size; i++) {
      const positions = new Float32Array(this.knockbackDustParticleCount * 3);
      const velocities = new Float32Array(this.knockbackDustParticleCount * 3);
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

      const uProgress = uniform(0);

      const material = new THREE.PointsNodeMaterial({
        transparent: true,
        depthWrite: false,
        // Dust occludes — NormalBlending reads the right way against bloom.
        blending: THREE.NormalBlending,
      });
      const { sizeNode, colorNode } = buildKnockbackDustNodes(uProgress);
      material.sizeNode = sizeNode;
      material.colorNode = colorNode;

      const points = new THREE.Points(geometry, material);
      points.visible = false;
      this.scene.add(points);
      this.knockbackDustPool.push({
        obj: points,
        material,
        geometry,
        positions,
        velocities,
        uProgress,
        active: false,
        life: 0,
        maxLife: 0.4,
      });
    }
  }

  private acquireKnockbackDust(): KnockbackDustEffect {
    const { effect, nextCursor } = this.acquireFromPool(
      this.knockbackDustPool,
      this.knockbackDustCursor,
    );
    this.knockbackDustCursor = nextCursor;
    return effect;
  }

  private deactivateKnockbackDust(effect: KnockbackDustEffect): void {
    effect.active = false;
    effect.obj.visible = false;
  }

  private createImpactDebrisPool(size: number): void {
    for (let i = 0; i < size; i++) {
      const positions = new Float32Array(this.impactDebrisParticleCount * 3);
      const velocities = new Float32Array(this.impactDebrisParticleCount * 3);
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

      const uProgress = uniform(0);
      const uColor = uniform(new THREE.Color(0xff9438));

      const material = new THREE.PointsNodeMaterial({
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });
      const { sizeNode, colorNode } = buildImpactDebrisNodes(uProgress, uColor);
      material.sizeNode = sizeNode;
      material.colorNode = colorNode;
      setEmissiveNode(material, colorNode);

      const points = new THREE.Points(geometry, material);
      points.visible = false;
      this.scene.add(points);
      this.impactDebrisPool.push({
        obj: points,
        material,
        geometry,
        positions,
        velocities,
        uProgress,
        uColor,
        active: false,
        life: 0,
        maxLife: 0.25,
      });
    }
  }

  private acquireImpactDebris(): ImpactDebrisEffect {
    const { effect, nextCursor } = this.acquireFromPool(
      this.impactDebrisPool,
      this.impactDebrisCursor,
    );
    this.impactDebrisCursor = nextCursor;
    return effect;
  }

  private deactivateImpactDebris(effect: ImpactDebrisEffect): void {
    effect.active = false;
    effect.obj.visible = false;
  }

  private createShieldShimmerPool(size: number): void {
    for (let i = 0; i < size; i++) {
      // Sphere geometry mirrors {@link createShieldPool} so the shimmer
      // wraps the target volume rather than projecting flat on the floor.
      const geometry = new THREE.SphereGeometry(0.55, 14, 10);
      const uProgress = uniform(0);

      const material = new THREE.MeshBasicNodeMaterial({
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });
      const { colorNode } = buildShieldShimmerNodes(uProgress);
      material.colorNode = colorNode;
      setEmissiveNode(material, colorNode);

      const mesh = new THREE.Mesh(geometry, material);
      mesh.visible = false;
      this.scene.add(mesh);
      this.shieldShimmerPool.push({
        obj: mesh,
        material,
        uProgress,
        active: false,
        life: 0,
        maxLife: 0.32,
      });
    }
  }

  private acquireShieldShimmer(): ShieldShimmerEffect {
    const { effect, nextCursor } = this.acquireFromPool(
      this.shieldShimmerPool,
      this.shieldShimmerCursor,
    );
    this.shieldShimmerCursor = nextCursor;
    return effect;
  }

  private deactivateShieldShimmer(effect: ShieldShimmerEffect): void {
    effect.active = false;
    effect.obj.visible = false;
  }

  private createDirectionalFlashPool(size: number): void {
    for (let i = 0; i < size; i++) {
      const geometry = new THREE.SphereGeometry(0.55, 12, 8);
      const uProgress = uniform(0);
      const uImpactDir = uniform(new THREE.Vector3(0, 0, 0));

      const material = new THREE.MeshBasicNodeMaterial({
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });
      const { colorNode } = buildDirectionalFlashNodes(uProgress, uImpactDir);
      material.colorNode = colorNode;
      setEmissiveNode(material, colorNode);

      const mesh = new THREE.Mesh(geometry, material);
      mesh.visible = false;
      this.scene.add(mesh);
      this.directionalFlashPool.push({
        obj: mesh,
        material,
        uProgress,
        uImpactDir,
        active: false,
        life: 0,
        maxLife: 0.24,
      });
    }
  }

  private acquireDirectionalFlash(): DirectionalFlashEffect {
    const { effect, nextCursor } = this.acquireFromPool(
      this.directionalFlashPool,
      this.directionalFlashCursor,
    );
    this.directionalFlashCursor = nextCursor;
    return effect;
  }

  private deactivateDirectionalFlash(effect: DirectionalFlashEffect): void {
    effect.active = false;
    effect.obj.visible = false;
  }
}
